import React, { useState } from "react";
import {
	Box,
	Button,
	Divider,
	IconButton,
	Modal,
	Typography,
} from "@mui/material";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import { useContext, useEffect } from "react";
import CityContext from "../../contexts/CityContext";
import { MapViewContext } from "../../contexts/MapViewContext";
import Graphic from "@arcgis/core/Graphic";
import EvaluatorContext from "../../contexts/EvaluatorContext";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Bar } from "react-chartjs-2";
import {
	ageGroupInServiceArea,
	ageLabels,
	demographicsInServiceArea,
	demoLabels,
	incomeGroupInServiceArea,
	incomeLabels,
	options,
} from "./EvaluatorResultChartInfo";
import {
	gpPDF_URL,
	dataFields
} from "../../constants";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import CloseIcon from "@mui/icons-material/Close";
import { evaluatorReport } from "../../reports/evaluatorReport";
import { trackEvent } from "../../hooks/useAnalytics";

const style: React.CSSProperties = {
	zIndex: 1,
	width: "30vw",
	background: "white",
	position: "absolute",
	right: "0",
};

const modalStyle = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};

export default function EvaluatorResulPanel() {
	const { city, urban } = useContext(CityContext);

	// Since ths summary statistics for a user-created park or trail are generated dynamically using the GP Service (as opposed to stored as part of the feature for existing parks, places, and urban areas), they are stored here as individual variables.
	const [totWalkDist, setTotWalkDist] = useState<number>(0);
	const [totalArea, setTotalArea] = useState<number>(0);
	const [totalLength, setTotalLength] = useState<number>(0);
	const [newPctServ, setNewPctServ] = useState<number>(0);
	const [oldPctServ, setOldPctServ] = useState<number>(0);
	const [newNotServPop, setNewNotServPop] = useState<number>(0);
	const [oldNotServPop, setOldNotServPop] = useState<number>(0);
	const [newServPop, setNewServPop] = useState<number>(0);
	const [totServPop, setTotServPop] = useState<number>(0);

	// These context values are used to store the values related to evaluating user-drawn shapes.
	// gpArray stores a feature set representing the 10-minute walk area generated by the GP Service based on the submitted shapes
	// parkArray stores a feature set representing the park shape(s) (polygons) drawn by the user
	// trailArray stores a feature set representing the trail shape(s) (polylines) drawn by the user
	const { gpArray, gpJson, parkArray, trailArray } = useContext(EvaluatorContext);

	// These three state variables store data related to the GP Service request.
	// jobMessage is the current message returned from the GP Service while it is in progress and displayed to the user
	// pdfUrl is returned when the service is complete and is the location of the resulting report
	// gpSuccess handles whether the report request was successful or not
	const [jobMessage, setJobMessage] = React.useState<string>("");
	const [pdfUrl, setPdfUrl] = React.useState<string>("");
	const [gpSuccess, setGPSuccess] = React.useState(false);

	const view = useContext(MapViewContext);

	// The `open` state and the two following functions are used to control whether or not the modal is displayed
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// The incomeData, ageData, and demoData state variables are used to store the respective datasets used to generate the charts
	const [demoData, setDemoData] = useState<any>({
		labels: demoLabels,
		datasets: [
			{
				label: "Current residents",
				data: [0, 0, 0, 0, 0, 0, 0, 0],
				borderColor: "#BEDA58",
				backgroundColor: "#BEDA58",
			},
		],
	});

	const [ageData, setAgeData] = useState<any>({
		labels: ageLabels,
		datasets: [
			{
				label: "age",
				data: [0, 0, 0, 0, 0, 0, 0, 0],
				borderColor: "#BEDA58",
				backgroundColor: "#BEDA58",
			},
		],
	});

	const [incomeData, setIncomeData] = useState<any>({
		labels: demoLabels,
		datasets: [
			{
				label: "income",
				data: [0, 0, 0, 0, 0, 0],
				borderColor: "#BEDA58",
				backgroundColor: "#BEDA58",
			},
		],
	});

	useEffect(() => {
		if (gpArray && city) {
			const walkAreaGL: any = view?.map.findLayerById("walk-layer");

			// any attributes, e.g. SUM_TOTPOPSVCA, referencing gpJson are not converted using dataFields since they come
			// from the GP service where they are all caps and are not affected by any underlying database change, e.g. PG

			//const totalWalkingDistance = gpArray[3]?.fs[0]?.attributes["SUM_TOTPOPSVCA"];
			const totalWalkingDistance = gpJson["OUTPUT_TOTAL_RS"]?.fs[0]?.attributes["SUM_TOTPOPSVCA"];
			const oldServPop: number = city.attributes[dataFields["SUM_TOTPOPSVCA"]];
			//const _newServPop: number = gpArray[1]?.fs[0]?.attributes["SUM_TOTPOPSVCA"];
			const _newServPop: number = gpJson["OUTPUT_NEW_RS"]?.fs[0]?.attributes["SUM_TOTPOPSVCA"];
			const totPop: number = city.attributes[dataFields["SUM_TPL_TOT_POP"]];
			const newPctServNum: number = ((oldServPop + _newServPop) / totPop) * 100;

			setTotWalkDist(totalWalkingDistance);
			setOldPctServ((oldServPop / totPop) * 100);
			setNewPctServ(newPctServNum);

			setNewNotServPop(totPop - oldServPop);
			setOldNotServPop(totPop - oldServPop - _newServPop);

			setNewServPop(_newServPop);
			setTotServPop(_newServPop + oldServPop);

			setDemoData({
				labels: demoLabels,
				datasets: [
					{
						label: " Existing Access",
						data: demographicsInServiceArea.map((x: any) =>
							//Math.round(gpArray[3].fs[0].attributes[x.new] - gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0].attributes[x.new] - gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#BEDA58",
						backgroundColor: "#BEDA58",
					},
					{
						label: " New Access",
						data: demographicsInServiceArea.map((x: any) =>
							//Math.round(gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#5DBA54",
						backgroundColor: "#5DBA54",
					},
				],
			});

			setAgeData({
				labels: ageLabels,
				datasets: [
					{
						label: " Existing Access",
						data: ageGroupInServiceArea.map((x: any) =>
							//Math.round(gpArray[3].fs[0].attributes[x.new] - gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0].attributes[x.new] - gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#BEDA58",
						backgroundColor: "#BEDA58",
					},
					{
						label: " New Access",
						data: ageGroupInServiceArea.map((x: any) =>
							//Math.round(gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#5DBA54",
						backgroundColor: "#5DBA54",
					},
				],
			});

			setIncomeData({
				labels: incomeLabels,
				datasets: [
					{
						label: " Existing Access",
						data: incomeGroupInServiceArea.map((x: any) =>
							//Math.round(gpArray[3].fs[0].attributes[x.new] - gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0].attributes[x.new] - gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#BEDA58",
						backgroundColor: "#BEDA58",
					},
					{
						label: " New Access",
						data: incomeGroupInServiceArea.map((x: any) =>
							//Math.round(gpArray[1].fs[0].attributes[x.new])
							Math.round(gpJson["OUTPUT_NEW_RS"].fs[0].attributes[x.new])
						),
						borderColor: "#5DBA54",
						backgroundColor: "#5DBA54",
					},
				],
			});

			walkAreaGL.graphics.reorder(walkAreaGL, 1);
		}
	}, [city]);

	useEffect(() => {
		if (parkArray) {
			//console.log(parkArray);
			let areaSum = 0;
			parkArray.forEach((park: Graphic) => {
				//console.log(park.geometry);
				// @ts-ignore
				areaSum += geometryEngine.geodesicArea(park.geometry, "acres");
				//console.log(totalArea);
			});
			setTotalArea(areaSum);
		}
	}, [totalArea]);

	useEffect(() => {
		if (trailArray) {
			let lengthSum = 0;
			trailArray.forEach((trail: Graphic) => {
				//console.log(trail.geometry);
				// @ts-ignore
				lengthSum += geometryEngine.geodesicLength(trail.geometry, "miles");
				//console.log(totalLength);
			});
			setTotalLength(lengthSum);
		}
	}, [totalLength]);

	const convertAttributeFieldNames = (o: any) => {
		let fields = Object.keys(dataFields)
		let values = Object.values(dataFields)

		// update attribute fields to use SQL server names since hard-coded in python GP service  
		let stats: any = {}
		Object.keys(o).forEach((k) => {
			let key = k
			let i = values.indexOf(k)
			if (i >= 0) {
				key = fields[i]
			}
			stats[key] = o[k]
		})
		return stats
	}

	// This function submits the job and required parameters to the print service
	const submitJob = async () => {
		//console.log(urban);
		handleOpen();
		setGPSuccess(false);

		const newReport = evaluatorReport;

		// area table
		newReport.tables.Area.body.fields[0].value = Math.round(
			//gpArray[3].fs[0]?.attributes[newReport.tables.Population.body.fields[0].name]
			gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Population.body.fields[0].name]
		);

		newReport.tables.Area.body.fields[0].newvalue = Math.round(
			//gpArray[1].fs[0]?.attributes[newReport.tables.Population.body.fields[0].name]
			gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Population.body.fields[0].name]
		);
		// population table
		newReport.tables.Population.body.fields[0].value = Math.round(
			//gpArray[3].fs[0]?.attributes["SUM_TOTPOPSVCA"]
			gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes["SUM_TOTPOPSVCA"]
		);
		newReport.tables.Population.body.fields[0].newvalue = Math.round(
			//gpArray[1].fs[0]?.attributes["SUM_TOTPOPSVCA"]
			gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes["SUM_TOTPOPSVCA"]
		);
		// age table
		newReport.tables.Age.body.fields.forEach((field, i) => {
			//newReport.tables.Age.body.fields[i].value = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Age.body.fields[i].name]
			newReport.tables.Age.body.fields[i].value = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Age.body.fields[i].name]
			);
			//newReport.tables.Age.body.fields[i].percentvalue = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Age.body.fields[i].percent]
			newReport.tables.Age.body.fields[i].percentvalue = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Age.body.fields[i].percent]
			);
			//newReport.tables.Age.body.fields[i].newvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Age.body.fields[i].name]
			newReport.tables.Age.body.fields[i].newvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Age.body.fields[i].name]
			);
			//newReport.tables.Age.body.fields[i].newpercentvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Age.body.fields[i].percent]);
			newReport.tables.Age.body.fields[i].newpercentvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Age.body.fields[i].percent]);
		});
		// income table
		newReport.tables.Income.body.fields.forEach((field, i) => {
			//newReport.tables.Income.body.fields[i].value = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Income.body.fields[i].name]);
			newReport.tables.Income.body.fields[i].value = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Income.body.fields[i].name]);
			
			
			//newReport.tables.Income.body.fields[i].percentvalue = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Income.body.fields[i].percent]);
			newReport.tables.Income.body.fields[i].percentvalue = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Income.body.fields[i].percent]);
			
			
			//newReport.tables.Income.body.fields[i].newvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Income.body.fields[i].name]);
			newReport.tables.Income.body.fields[i].newvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Income.body.fields[i].name]);
			
			//newReport.tables.Income.body.fields[i].newpercentvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Income.body.fields[i].percent]);
			newReport.tables.Income.body.fields[i].newpercentvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Income.body.fields[i].percent]);
		});
		// race table
		newReport.tables.Race.body.fields.forEach((field, i) => {
			//newReport.tables.Race.body.fields[i].value = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Race.body.fields[i].name]);
			newReport.tables.Race.body.fields[i].value = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Race.body.fields[i].name]);
			
			//newReport.tables.Race.body.fields[i].percentvalue = Math.round(gpArray[3].fs[0]?.attributes[newReport.tables.Race.body.fields[i].percent]);
			newReport.tables.Race.body.fields[i].percentvalue = Math.round(gpJson["OUTPUT_TOTAL_RS"].fs[0]?.attributes[newReport.tables.Race.body.fields[i].percent]);
			
			//newReport.tables.Race.body.fields[i].newvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Race.body.fields[i].name]);
			newReport.tables.Race.body.fields[i].newvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Race.body.fields[i].name]);
			
			//newReport.tables.Race.body.fields[i].newpercentvalue = Math.round(gpArray[1].fs[0]?.attributes[newReport.tables.Race.body.fields[i].percent]);
			newReport.tables.Race.body.fields[i].newpercentvalue = Math.round(gpJson["OUTPUT_NEW_RS"].fs[0]?.attributes[newReport.tables.Race.body.fields[i].percent]);
		});

		// other parameters
		const oldservpop: number = city?.attributes[dataFields["SUM_TOTPOPSVCA"]];

		//const newservpop: number = oldservpop + gpArray[1].fs[0].attributes["SUM_TOTPOPSVCA"];
		const newservpop: number = oldservpop + gpJson["OUTPUT_NEW_RS"].fs[0].attributes["SUM_TOTPOPSVCA"];
		const totpop: number = city?.attributes[dataFields["SUM_TPL_TOT_POP"]];
		const oldpctserv = Math.round((oldservpop / totpop) * 1000) / 10;
		const newpctserv = Math.round((newservpop / totpop) * 1000) / 10;

		if (city) {
			city.attributes["OLD_POP_SERVED"] = oldservpop;
			city.attributes["OLD_POP_SERVED_PCT"] = oldpctserv;
			city.attributes["NEW_POP_SERVED"] = Math.round(newservpop);
			city.attributes["NEW_POP_SERVED_PCT"] = newpctserv;
		}

		newReport.CityOrUrbanID = city?.attributes[dataFields["PLACE_ID"]];

		//newReport.PlaceStats = city?.attributes;
		//newReport.UrbanStats = urban?.attributes;

		// update attribute fields to use SQL server names since hard-coded in python GP service 
		newReport.PlaceStats = convertAttributeFieldNames(city?.attributes);
		newReport.UrbanStats = convertAttributeFieldNames(urban?.attributes);

		//newReport.NewPlaceStats = gpArray[4];
		newReport.NewPlaceStats = gpJson["OUTPUT_PLACE_RS"]
		console.log(newReport);

		const report_json = {
			report: newReport,
		};

		var gpParams = {
			INPUT_REPORT_JSON: JSON.stringify(report_json),
		};

		const parkServiceAreaFeatureSet = new FeatureSet({
			displayFieldName: "Name",
			geometryType: "polygon",
			spatialReference: {
				wkid: 102100,
			},
			fields: [
				{
					name: "OBJECTID",
					type: "oid",
					alias: "OBJECTID",
				},
				{
					name: "Name",
					type: "string",
					alias: "Service Area Name",
					length: 150,
				},
			],
			//features: gpArray[2].fs,
			features: gpJson["OUTPUT_SERVICE_AREAS_FS"].fs,
			exceededTransferLimit: false,
		});

		const parkFeatureSet = new FeatureSet({
			displayFieldName: "Name",
			geometryType: "polygon",
			spatialReference: {
				wkid: 102100,
			},
			fields: [
				{
					name: "OBJECTID",
					type: "oid",
					alias: "OBJECTID",
				},
				{
					name: "Name",
					type: "string",
					alias: "Park Name",
					length: 150,
				},
			],
			features: parkArray,
			exceededTransferLimit: false,
		});

		const trailFeatureSet = new FeatureSet({
			displayFieldName: "Name",
			geometryType: "polyline",
			spatialReference: {
				wkid: 102100,
			},
			fields: [
				{
					name: "OBJECTID",
					type: "oid",
					alias: "OBJECTID",
				},
				{
					name: "Name",
					type: "string",
					alias: "Park Name",
					length: 150,
				},
			],
			features: trailArray,
			exceededTransferLimit: false,
		});

		// @ts-ignore
		//if (gpArray[2].fs.length > 0) {
		if (gpJson["OUTPUT_SERVICE_AREAS_FS"].fs.length > 0) {
			// @ts-ignore
			gpParams["INPUT_SERVICEAREA_FS"] = parkServiceAreaFeatureSet;
		}
		// @ts-ignore
		if (parkArray.length > 0) {
			// @ts-ignore
			gpParams["INPUT_PARK_FS"] = parkFeatureSet;
		}
		// @ts-ignore
		if (trailArray.length > 0) {
			// @ts-ignore
			gpParams["INPUT_TRAIL_FS"] = trailFeatureSet;
		}

		const jobInfo: JobInfo = await geoprocessor.submitJob(gpPDF_URL, gpParams);

		// The callback below monitors the progress of the GP Service to be communicated to the user
		const options = {
			interval: 3000,
			statusCallback: (j: any) => {
				if (j.messages.length) {
					setJobMessage(j.messages[j.messages.length - 1].description);
					console.log("Job Status: ", j.jobStatus);
					console.log(
						"Job Message: ",
						j.messages[j.messages.length - 1].description
					);
				} else {
					setJobMessage(
						"Unable to contact server.  Please wait or try again later."
					);
				}
				if (j.jobStatus === "esriJobFailed") {
					console.log("Error: Job failed.");
				}
			},
		};

		jobInfo.waitForJobCompletion(options).then((resp: any) => {
			getJobResultData(jobInfo);
		});

		// This function is triggered once the job is complete
		const getJobResultData = (jobInfo: JobInfo) => {
			console.log(jobInfo);
			jobInfo.fetchResultData("output_file").then((r: any) => {
				setGPSuccess(true);
				setPdfUrl(r.value.url);
				const parkLayer: any = view?.map.findLayerById("sketch-layer");
				parkLayer.graphics.reorder(parkLayer, 0);
			});
		};
	};

	return (
		<Box
			height="100vh"
			bgcolor="white"
			boxSizing="border-box"
		>
			<Typography
				sx={{ textAlign: "center" }}
				variant="h1"
				color="earth.main"
				gutterBottom
			>
				Impact report
			</Typography>
			<Box
				display="flex"
				justifyContent="center"
			>
				<Button
					variant="contained"
					onClick={() => {
						trackEvent("export_statistics_click", "evaluator");
						submitJob();
					}}
				>
					<Typography variant="link">Export Access Statistics PDF</Typography>
				</Button>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Park Statistics
			</Typography>

			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				sx={{ marginTop: "15px", marginBottom: "5px", marginLeft: "10px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					width="110px"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Number of Parks
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{parkArray ? parkArray.length : 0}
					</Typography>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					width="110px"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Park Acres
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{totalArea.toFixed(1)}
					</Typography>
				</Box>
			</Box>
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				sx={{ marginTop: "10px", marginBottom: "5px", marginLeft: "10px" }}
			>
				<Box
					display="flex"
					flexDirection="column"
					width="110px"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Number of Trails
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{trailArray ? trailArray.length : 0}
					</Typography>
				</Box>
				<Box
					display="flex"
					flexDirection="column"
					width="110px"
				>
					<Typography
						variant="subtitle2"
						color=""
					>
						Total Trail Length (miles)
					</Typography>
					<Typography
						variant="h3"
						color=""
					>
						{totalLength.toFixed(1)}
					</Typography>
				</Box>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				City-level Park Access Impact
			</Typography>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
				}}
			>
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginBottom: "10px", marginLeft: "10px" }}
					>
						Percent of city population within a 10-minute walk of a park
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "center",
							marginBottom: "10px",
							marginLeft: "10px",
						}}
					>
						<Typography
							variant="h3"
							color=""
						>
							{oldPctServ.toFixed(1)}%
						</Typography>
						<ArrowForwardIcon />
						<Typography
							variant="h3"
							color="#00b0f0"
						>
							{newPctServ.toFixed(1)}%
						</Typography>
					</Box>
				</Box>

				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
				>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginLeft: "10px" }}
					>
						City population outside of a 10-minute walk
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginBottom: "10px", marginLeft: "10px" }}
					>
						Total Population:{" "}
						{city?.attributes[dataFields["SUM_TPL_TOT_POP"]].toLocaleString("en-US")}
					</Typography>{" "}
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "center",
						}}
					>
						<Typography variant="h3">
							{parseFloat(newNotServPop.toFixed(0)).toLocaleString("en-US")}
						</Typography>
						<ArrowForwardIcon />
						<Typography
							variant="h3"
							color="#00b0f0"
						>
							{parseFloat(oldNotServPop.toFixed(0)).toLocaleString("en-US")}
						</Typography>
					</Box>
				</Box>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h5"
				color="grass.main"
				sx={{ marginBottom: "10px" }}
			>
				Project Access Statistics
			</Typography>

			<Box sx={{ marginLeft: "10px" }}>
				<Box
					display="flex"
					flexDirection="column"
					justifyContent="center"
					//alignItems="center"
					sx={{ marginTop: "15px", marginBottom: "15px" }}
				>
					<Typography
						variant="subtitle2"
						textAlign="left"
						color=""
					>
						Total population within a 10-minute walk of the new parks and/or
						trails:
					</Typography>
					<Typography
						variant="h3"
						color=""
						textAlign="center"
						marginTop="7px"
						marginBottom="7px"
					>
						{parseFloat(totWalkDist.toFixed(0)).toLocaleString("en-US")}
					</Typography>
					<Typography
						textAlign="left"
						variant="subtitle2"
						color=""
					>
						Of this total, we calculate the number of people that live within a
						10-minute walk of an existing park or trail (“Existing access”) and
						those that do not (“New access”):
					</Typography>
				</Box>

				<Box
					display="flex"
					flexDirection="row"
					justifyContent="space-evenly"
					sx={{ marginTop: "5px", marginBottom: "5px" }}
				>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
					>
						<Typography
							variant="subtitle2"
							color=""
							fontWeight="bold"
						>
							Existing access
						</Typography>
						<Typography
							variant="h3"
							color="#beda58"
						>
							{parseFloat((totWalkDist - newServPop).toFixed(0)).toLocaleString(
								"en-US"
							)}
						</Typography>
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
					>
						<Typography
							variant="subtitle2"
							color=""
							fontWeight="bold"
						>
							New access
						</Typography>
						<Typography
							variant="h3"
							color="#5dba54"
						>
							{parseFloat(newServPop.toFixed(0)).toLocaleString("en-US")}
						</Typography>
					</Box>
				</Box>
			</Box>
			{newServPop < 1 ? (
				<Typography
					variant="h5"
					color="#F7931E"
					sx={{ marginBottom: "10px", marginTop: "15px" }}
				>
					This park either does not allow public access or has zero population
					with new access within a 10-minute walk
				</Typography>
			) : (
				<div>
					<Divider sx={{ marginTop: "25px" }} />
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Race/Ethnicity
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginLeft: "10px" }}
					>
						Number of residents within a 10-minute walk by race/ethnicity.
						*Excludes those that report Hispanic origin (which is captured
						separately from race by the U.S. Census).
					</Typography>
					<Divider />

					<Box
						height="400px"
						width="100%"
					>
						<Bar
							options={options(demoLabels)}
							data={demoData}
						/>
					</Box>
					<Legend />
					<Divider sx={{ marginTop: "25px" }} />
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Age
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginLeft: "10px" }}
					>
						Number of residents within a 10-minute walk by age group
					</Typography>

					<Divider />
					<Box
						height="200px"
						width="100%"
					>
						<Bar
							options={options(ageLabels)}
							data={ageData}
						/>
					</Box>
					<Legend />
					<Divider sx={{ marginTop: "25px" }} />
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Income
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
						sx={{ marginLeft: "10px" }}
					>
						Number of households within a 10-minute walk by income. MAI = Median
						Area Income, which is the median income of the urban area.
					</Typography>

					<Divider />
					<Box
						height="200px"
						width="100%"
						id="evaluator_scroll"
					>
						<Bar
							options={options(incomeLabels)}
							data={incomeData}
						/>
					</Box>
					<Legend />
				</div>
			)}
			<Divider sx={{ marginTop: "25px", marginBottom: "25px" }} />
			<Typography
				variant="h6"
				color="headingGrey.main"
				textAlign="left"
			>
				Demographic information sourced from Esri 2025
			</Typography>

			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={modalStyle}>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
					{!gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Generating PDF report...
							</Typography>
							<Typography
								id="modal-modal-description"
								sx={{ mt: 2 }}
							>
								{jobMessage}
							</Typography>
						</>
					)}
					{gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Success!
							</Typography>
							<Button
								variant="contained"
								sx={{ marginTop: "10px" }}
								href={pdfUrl}
								target="_blank"
								onClick={() => {
									trackEvent("export_statistics_pdf", "evaluator");
								}}
							>
								Download PDF Report
							</Button>
						</>
					)}
				</Box>
			</Modal>
		</Box>
	);
}

const Legend = () => {
	return (
		<Box
			marginTop="10px"
			display="flex"
			justifyContent="center"
			alignItems="center"
			width="100%"
			gap="5px"
		>
			<Box
				style={{
					paddingLeft: "5px",
					paddingRight: "5px",
					display: "flex",
					alignItems: "center",
					background: "#beda58",
					height: "30px",
				}}
			>
				<Typography
					fontFamily="Arial"
					color=""
				>
					Existing Access
				</Typography>
			</Box>
			<Box
				style={{
					paddingLeft: "5px",
					paddingRight: "5px",
					background: "#5dba54",
					display: "flex",
					alignItems: "center",
					height: "30px",
				}}
			>
				<Typography
					fontFamily="Arial"
					color=""
				>
					New Access
				</Typography>
			</Box>
		</Box>
	);
};
